import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber, Button, Space, Typography, Divider, message, Input, AutoComplete } from 'antd';
import { EditOutlined, UserOutlined, PrinterOutlined, DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Text } = Typography;
const { Option } = Select;

const BulkEditModal = ({
    visible,
    onCancel,
    onConfirm,
    selectedOrderItems = [],
    userOptions = [],
    printerOptions = [],
    loading = false
}) => {
    const [form] = Form.useForm();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [noteTemplates, setNoteTemplates] = useState([]);

    // Fetch note templates when modal opens
    useEffect(() => {
        if (visible) {
            form.resetFields();
            fetchNoteTemplates();
        }
    }, [visible, form]);

    const fetchNoteTemplates = async () => {
        try {
            const response = await api.get('/note-templates?limit=50&orderBy=usageCount&orderDirection=DESC');
            if (response.data.success) {
                const templates = response.data.data.map(template => ({
                    value: template.content,
                    label: template.title,
                    category: template.category
                }));
                setNoteTemplates(templates);
            }
        } catch (error) {
            console.error('Error fetching note templates:', error);
        }
    };

    const handleConfirm = async () => {
        try {
            const values = await form.validateFields();
            
            // Filter out empty values
            const updateData = {};
            if (values.supporterId !== undefined) {
                updateData.supporterId = values.supporterId;
            }
            if (values.printerId !== undefined) {
                updateData.printerId = values.printerId;
            }
            if (values.baseCost !== undefined) {
                updateData.baseCost = values.baseCost;
            }
            if (values.note !== undefined && values.note !== '') {
                updateData.note = values.note;
            }

            if (Object.keys(updateData).length === 0) {
                message.warning('Please select at least one field to update');
                return;
            }

            setConfirmLoading(true);
            await onConfirm(updateData);
            form.resetFields();
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setConfirmLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>Bulk Edit Order Items</span>
                </Space>
            }
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    loading={confirmLoading || loading}
                    onClick={handleConfirm}
                    disabled={selectedOrderItems.length === 0}
                >
                    Update {selectedOrderItems.length} Item{selectedOrderItems.length !== 1 ? 's' : ''}
                </Button>
            ]}
            width={600}
            destroyOnClose
        >
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    You are about to update <Text strong>{selectedOrderItems.length}</Text> selected order items.
                    Only fields with values will be updated.
                </Text>
            </div>

            <Divider />

            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                <Form.Item
                    name="supporterId"
                    label={
                        <Space>
                            <UserOutlined />
                            <span>Supporter</span>
                        </Space>
                    }
                    help="Select a supporter to assign to all selected items"
                >
                    <Select
                        placeholder="Select supporter (leave empty to skip)"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {userOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="printerId"
                    label={
                        <Space>
                            <PrinterOutlined />
                            <span>Printer</span>
                        </Space>
                    }
                    help="Select a printer to assign to all selected items"
                >
                    <Select
                        placeholder="Select printer (leave empty to skip)"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {printerOptions.map(option => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="baseCost"
                    label={
                        <Space>
                            <DollarOutlined />
                            <span>Base Cost</span>
                        </Space>
                    }
                    help="Set base cost for all selected items (in USD)"
                >
                    <InputNumber
                        placeholder="Enter base cost (leave empty to skip)"
                        min={0}
                        step={0.01}
                        precision={2}
                        style={{ width: '100%' }}
                        prefix="$"
                    />
                </Form.Item>

                <Form.Item
                    name="note"
                    label={
                        <Space>
                            <FileTextOutlined />
                            <span>Note</span>
                        </Space>
                    }
                    help="Add note to all selected items (type to see suggestions)"
                >
                    <AutoComplete
                        placeholder="Enter note (leave empty to skip)"
                        style={{ width: '100%' }}
                        options={noteTemplates.map(template => ({
                            value: template.value,
                            label: (
                                <div>
                                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                        {template.label}
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {template.value}
                                    </div>
                                    {template.category && (
                                        <span style={{ fontSize: '10px', color: '#999', background: '#f0f0f0', padding: '1px 4px', borderRadius: '2px' }}>
                                            {template.category}
                                        </span>
                                    )}
                                </div>
                            )
                        }))}
                        filterOption={(inputValue, option) =>
                            option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                        }
                        dropdownStyle={{ maxHeight: 200 }}
                    />
                </Form.Item>
            </Form>

            <Divider />

            <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <strong>Note:</strong> Only the fields you fill in will be updated. 
                    Leave fields empty if you don't want to change them.
                </Text>
            </div>
        </Modal>
    );
};

export default BulkEditModal;
